/**
 * Bilingual blog data — 6 articles (3 topics × ES & EN versions).
 *
 * Slugs end with `-es` or `-en` so Google indexes them as separate entries
 * with proper hreflang metadata (set in BlogPost.jsx via <link rel="alternate">).
 */

export const BLOG_POSTS = [
  // ──────────────── Topic 1: Choosing the right plan ────────────────
  {
    slug: "como-elegir-mejor-plan-luz-texas",
    lang: "es",
    altSlug: "how-to-choose-best-electricity-plan-texas",
    category: "Guías",
    title: "Cómo elegir el mejor plan de luz en Texas",
    excerpt: "Una guía honesta y bilingüe para entender la diferencia entre planes fijos, variables y de tasa indexada — y por qué casi siempre conviene el fijo.",
    cover: "https://images.unsplash.com/photo-1548613053-22087dd8edb8?auto=format&fit=crop&w=1600&q=70",
    author: "Equipo Velcor",
    publishedAt: "2026-01-12",
    readTime: 6,
    tags: ["plan-fijo", "guia", "tarifas"],
    body: `
## El error #1 al elegir un plan en Texas

La mayoría de hogares en Texas elige su plan de electricidad por una sola razón: el precio anunciado. Es lógico — pero también es la razón más común por la que la gente termina pagando el doble en julio.

Aquí hay 3 cosas que **realmente** importan al elegir un plan:

### 1. Tipo de tarifa: fijo, variable o indexado

- **Fijo**: el precio por kWh no cambia durante todo el contrato. Es predecible. Es lo que ofrecemos en Velcor.
- **Variable**: el precio cambia mes a mes. Empieza barato, sube en verano. Casi siempre te perjudica.
- **Indexado**: el precio sigue un índice del mercado mayorista. Volátil. No recomendado para uso residencial.

### 2. Cargos ocultos en la EFL

La **Electricity Facts Label (EFL)** es el documento que la PUCT obliga a todo REP a entregarte antes de inscribirte. Léelo. Busca:

- Tarifa base mensual (¿hay una "service fee" cuando tu uso es bajo?)
- Cargos de la TDU (CenterPoint / Oncor / AEP / TNMP) — siempre incluidos por ley
- Fee de cancelación anticipada (en planes con contrato largo)

> **Tip:** Velcor no cobra fee de cancelación. Cancelas cuando quieras.

### 3. Promedio realista a 1,000 kWh

La EFL muestra el "precio promedio por kWh" a 500, 1,000 y 2,000 kWh. Mira el de **1,000 kWh** — es lo más cercano al uso típico residencial en Texas. Es el número que debes comparar entre proveedores.

## Tres preguntas para hacerle a tu REP

1. ¿La tarifa que me están ofreciendo es fija por todo el período?
2. ¿Hay un cargo mensual base que aplique aunque no consuma energía?
3. ¿Cuánto cuesta cancelar antes del término del contrato?

Si las tres respuestas no son claras y por escrito, busca otro proveedor.

## ¿Por qué Velcor?

Porque damos un único plan: tarifa fija, sin contrato a largo plazo, sin fee de cancelación, sin depósito. Una sola factura mensual que incluye TDU + impuestos. Y si tienes dudas, te atiende un humano en español al (800) 715-6516.
`,
  },
  {
    slug: "how-to-choose-best-electricity-plan-texas",
    lang: "en",
    altSlug: "como-elegir-mejor-plan-luz-texas",
    category: "Guides",
    title: "How to choose the best electricity plan in Texas",
    excerpt: "An honest, bilingual guide to fixed, variable and indexed plans — and why fixed almost always wins.",
    cover: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1600&q=70",
    author: "Velcor Team",
    publishedAt: "2026-01-12",
    readTime: 6,
    tags: ["fixed-rate", "guide", "rates"],
    body: `
## The #1 mistake when picking a Texas plan

Most Texas households pick a plan for a single reason: the headline price. It's logical — but it's also the #1 reason people end up paying double in July.

Here are the 3 things that **actually** matter:

### 1. Rate type: fixed, variable, or indexed

- **Fixed**: the per-kWh price doesn't change during the contract. Predictable. This is what Velcor offers.
- **Variable**: the price changes month-to-month. Starts cheap, jumps in summer. Almost always works against you.
- **Indexed**: tracks a wholesale market index. Volatile. Not recommended for residential use.

### 2. Hidden fees in the EFL

The **Electricity Facts Label (EFL)** is the PUCT-mandated disclosure every REP must give you before signup. Read it. Look for:

- Monthly base charge (is there a "service fee" when your usage is low?)
- TDU charges (CenterPoint / Oncor / AEP / TNMP) — always included by law
- Early termination fee (on long-contract plans)

> **Tip:** Velcor charges no cancellation fee. Cancel any time.

### 3. Realistic 1,000 kWh average

The EFL shows the "average price per kWh" at 500, 1,000 and 2,000 kWh. Look at the **1,000 kWh** number — closest to typical Texas residential use. That's the figure to compare across providers.

## Three questions to ask your REP

1. Is the rate you're offering fixed for the entire term?
2. Is there a monthly base charge that applies even with no usage?
3. How much does it cost to cancel before the contract ends?

If all three answers aren't clear and in writing, find another provider.

## Why Velcor?

Because we offer a single plan: fixed rate, no long-term contract, no cancellation fee, no deposit. One monthly bill including TDU + taxes. And when you have questions, a real human answers in English or Spanish at (800) 715-6516.
`,
  },

  // ──────────────── Topic 2: TDU vs REP ────────────────
  {
    slug: "diferencia-tdu-rep-texas",
    lang: "es",
    altSlug: "tdu-vs-rep-explained",
    category: "Educación",
    title: "TDU vs REP: ¿quién te cobra realmente la luz?",
    excerpt: "Texas tiene un mercado eléctrico desregulado. Eso significa que dos compañías diferentes intervienen en tu factura. Te explicamos quién hace qué y por qué importa.",
    cover: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=1600&q=70",
    author: "Equipo Velcor",
    publishedAt: "2026-01-22",
    readTime: 5,
    tags: ["tdu", "rep", "puct", "mercado-electrico"],
    body: `
## Las dos compañías detrás de tu factura

En Texas, desde la desregulación de 2002, **dos compañías distintas** participan en que tu casa tenga luz:

### La TDU (Transmission and Distribution Utility)

Es dueña de los **postes, cables y transformadores** que llevan la electricidad desde la planta generadora hasta tu medidor. No la elegiste tú — la TDU te toca por geografía. En Texas hay 4 grandes:

| TDU | Cobertura |
|---|---|
| **CenterPoint Energy** | Houston y costa este |
| **Oncor** | Dallas-Fort Worth y norte de Texas |
| **AEP Texas** | Sur y oeste de Texas |
| **TNMP** | Áreas dispersas (Galveston, partes del valle del Río Grande) |

La TDU **no te cobra a ti directamente**. Le cobra al REP, y el REP te cobra a ti.

### El REP (Retail Electric Provider)

Aquí entras tú: el REP **es el que tú eliges**. Compite contra otros REPs por tu cuenta. Define tu tarifa por kWh, tu cargo base, tu plan. **Velcor es un REP**, certificado #VEL-2026 ante la PUCT.

## ¿Por qué importa esto?

Porque cuando algo falla, hay que saber a quién llamar:

- **¿Se fue la luz por una tormenta?** → Llama a tu TDU (CenterPoint, Oncor, etc.). Ellos arreglan postes.
- **¿Tienes una pregunta sobre tu factura o tu plan?** → Llama a tu REP. En Velcor: (800) 715-6516.
- **¿Quieres cambiar de proveedor?** → Solo cambias el REP. La TDU sigue siendo la misma.

## Lo que la PUCT obliga a todos los REPs

Sin importar qué REP elijas, la **Public Utility Commission of Texas (PUCT)** los obliga a:

1. Darte una **EFL** antes de inscribirte (precio promedio por kWh).
2. Darte un **YRAC** (Tus Derechos como Cliente) según § 25.475.
3. Incluir los cargos de la TDU dentro de tu factura única.
4. No cancelarte el servicio sin previo aviso por al menos 10 días.
5. Ofrecerte un **plan de pagos diferidos** si pides ayuda antes del corte.

## En resumen

- TDU = los cables (no la eliges, no llama nunca a tu casa).
- REP = quién te factura y te atiende (sí lo eliges, en Velcor te tratamos en español).
`,
  },
  {
    slug: "tdu-vs-rep-explained",
    lang: "en",
    altSlug: "diferencia-tdu-rep-texas",
    category: "Education",
    title: "TDU vs REP: who actually charges you for power?",
    excerpt: "Texas has a deregulated electricity market. That means two different companies show up on your bill. Here's who does what and why it matters.",
    cover: "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1600&q=70",
    author: "Velcor Team",
    publishedAt: "2026-01-22",
    readTime: 5,
    tags: ["tdu", "rep", "puct", "deregulation"],
    body: `
## The two companies behind your bill

In Texas, since the 2002 deregulation, **two separate companies** participate in lighting up your home:

### The TDU (Transmission and Distribution Utility)

It owns the **poles, wires and transformers** that move electricity from the power plant to your meter. You didn't pick it — the TDU is geography-based. There are 4 major TDUs in Texas:

| TDU | Coverage |
|---|---|
| **CenterPoint Energy** | Houston and east coast |
| **Oncor** | Dallas-Fort Worth and north Texas |
| **AEP Texas** | South and west Texas |
| **TNMP** | Scattered areas (Galveston, parts of the Rio Grande Valley) |

The TDU **doesn't bill you directly**. It bills the REP, and the REP bills you.

### The REP (Retail Electric Provider)

Here's where you come in: the REP **is the one you choose**. It competes against other REPs for your account. It sets your per-kWh rate, base charge, and plan. **Velcor is a REP**, PUCT-certified #VEL-2026.

## Why does this matter?

Because when something goes wrong, you need to know who to call:

- **Power's out from a storm?** → Call your TDU (CenterPoint, Oncor, etc.). They fix poles.
- **Question about your bill or plan?** → Call your REP. At Velcor: (800) 715-6516.
- **Want to switch providers?** → You only change the REP. The TDU stays the same.

## What PUCT mandates for every REP

No matter which REP you pick, the **Public Utility Commission of Texas (PUCT)** requires them to:

1. Give you an **EFL** before signup (average price per kWh).
2. Give you a **YRAC** (Your Rights as a Customer) per § 25.475.
3. Include the TDU charges inside your single bill.
4. Not disconnect service without at least a 10-day notice.
5. Offer you a **deferred payment plan** if you ask for help before disconnection.

## Bottom line

- TDU = the wires (you don't pick it, never calls your house).
- REP = who bills and supports you (you do pick it; at Velcor we treat you in Spanish or English).
`,
  },

  // ──────────────── Topic 3: 5 tips to lower bill ────────────────
  {
    slug: "5-tips-bajar-factura-luz-texas",
    lang: "es",
    altSlug: "5-tips-lower-electricity-bill-texas",
    category: "Tips de ahorro",
    title: "5 tips reales para bajar tu factura de luz en Texas",
    excerpt: "Sin gimmicks. Sin 'apaga las luces cuando salgas'. Cosas concretas que mueven la aguja en el calor de Texas.",
    cover: "https://images.unsplash.com/photo-1605007493699-af65834f8a00?auto=format&fit=crop&w=1600&q=70",
    author: "Equipo Velcor",
    publishedAt: "2026-02-02",
    readTime: 4,
    tags: ["ahorro", "consumo", "verano-texas"],
    body: `
## Antes de empezar

En Texas, el aire acondicionado consume **~50% de tu factura** en julio y agosto. Si quieres bajar tu cuenta, ahí está la pelea — no en cambiar focos.

## 1. Sube el termostato 2°F

De 72°F a 74°F suena mínimo, pero **cada grado adicional ahorra ~3% de energía** en climatización. En una factura de $200, son $12 al mes. Multiplica por 4 meses de verano: $48.

> Pro tip: con un Nest o Ecobee programado, ni notas la diferencia.

## 2. Sella las fugas de aire

El 25-40% del aire frío se escapa por:

- **Ventanas mal selladas** (usa weather stripping de $5)
- **Puertas exteriores** (instala un door sweep en la base)
- **Tomas eléctricas en paredes exteriores** (foam gaskets de $1)

Inversión total: ~$30. Ahorro típico: $20-40/mes en verano.

## 3. Cambia el filtro del A/C cada 2 meses

Un filtro tapado obliga al compresor a trabajar 15-20% más. Filtros estándar cuestan $5-15. Reemplazo de 2 minutos. Ahorro típico: $15/mes en pleno verano.

## 4. Pasa el lavado de ropa y secadora a la noche

La mayoría de TDUs en Texas (CenterPoint, Oncor) tienen tarifas más altas durante el "peak" de 3-7pm. Lavar y secar a las 9pm reduce el cargo de transmisión. Algunos REPs ofrecen planes "free nights" — Velcor no, porque preferimos darte una tarifa fija sin trampa.

## 5. Pide la lectura del medidor 2 veces al mes

En tu portal Velcor puedes ver tu consumo diario. Si un mes ves un salto inexplicable, **algo se rompió en tu casa** (más comúnmente: refri viejo, calentador de agua, una bomba de pool). Detectarlo a tiempo te ahorra $50-150 al mes.

## Lo que NO funciona

- ❌ Apagar electrónicos en stand-by (suena bonito; ahorras $1-3/mes en una casa promedio).
- ❌ Cambiar todos los focos por LED al mismo tiempo (los LEDs ya consumen poco; ROI lento).
- ❌ Plantar árboles para hacer sombra (aplaudible, pero tarda 5+ años).

Concentra tu energía donde está el dinero: el A/C.
`,
  },
  {
    slug: "5-tips-lower-electricity-bill-texas",
    lang: "en",
    altSlug: "5-tips-bajar-factura-luz-texas",
    category: "Saving tips",
    title: "5 real tips to lower your Texas electricity bill",
    excerpt: "No gimmicks. No 'turn off the lights when you leave'. Concrete things that move the needle in Texas heat.",
    cover: "https://images.unsplash.com/photo-1611365892117-bce8b540320b?auto=format&fit=crop&w=1600&q=70",
    author: "Velcor Team",
    publishedAt: "2026-02-02",
    readTime: 4,
    tags: ["savings", "consumption", "texas-summer"],
    body: `
## Before we start

In Texas, air conditioning eats **~50% of your bill** in July and August. If you want to lower your bill, that's where the fight is — not changing light bulbs.

## 1. Bump the thermostat 2°F

From 72°F to 74°F sounds minor, but **every degree saves ~3% on cooling energy**. On a $200 bill that's $12/month. Multiply by 4 summer months: $48.

> Pro tip: with a programmed Nest or Ecobee you barely notice the difference.

## 2. Seal air leaks

25-40% of cold air escapes through:

- **Poorly-sealed windows** (use $5 weather stripping)
- **Exterior doors** (install a base door sweep)
- **Electrical outlets on exterior walls** ($1 foam gaskets)

Total investment: ~$30. Typical savings: $20-40/month in summer.

## 3. Change the A/C filter every 2 months

A clogged filter forces the compressor to work 15-20% harder. Standard filters cost $5-15. 2-minute swap. Typical savings: $15/month in peak summer.

## 4. Move laundry & dryer to nighttime

Most Texas TDUs (CenterPoint, Oncor) charge higher rates during the 3-7pm "peak". Washing and drying at 9pm reduces transmission charges. Some REPs offer "free nights" plans — Velcor doesn't, because we'd rather give you a flat fixed rate without gimmicks.

## 5. Read your meter twice a month

In your Velcor portal you can see daily usage. If you spot an unexplained jump in a month, **something broke in your house** (most commonly: old fridge, water heater, pool pump). Catching it early saves $50-150/month.

## What does NOT work

- ❌ Unplugging stand-by electronics (sounds good; saves $1-3/month in a typical home).
- ❌ Replacing every bulb with LEDs at once (LEDs already use little; slow ROI).
- ❌ Planting shade trees (admirable, but takes 5+ years).

Focus your energy where the money is: the A/C.
`,
  },
];

export const BLOG_SLUGS = BLOG_POSTS.map((p) => p.slug);

export function getPostBySlug(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug) || null;
}

export function getRelatedPosts(post, limit = 3) {
  if (!post) return [];
  return BLOG_POSTS.filter(
    (p) => p.slug !== post.slug && p.lang === post.lang && p.category === post.category,
  ).slice(0, limit);
}

export function listPostsByLang(lang) {
  return BLOG_POSTS.filter((p) => p.lang === (lang || "es"));
}
